import { IconButton } from '@features/shared/components/IconButton'
import { CloseIcon, FileIcon, PdfFileIcon } from '@features/shared/icons/icons'
import type { PendingFile } from '@features/shared/hooks/useFileAttachments'
import { formatFileSize } from '@features/shared/utils/fileSize'

/** A file as a chip shows it; `previewUrl` is an image's picture. */
export type AttachmentChip = Pick<
  PendingFile<unknown>,
  'localId' | 'fileName' | 'size' | 'kind' | 'status' | 'progress' | 'error' | 'previewUrl'
>

function Preview({ item }: { item: AttachmentChip }) {
  if (item.kind === 'image' && item.previewUrl) {
    return <img src={item.previewUrl} alt="" className="h-9 w-9 shrink-0 rounded-md object-cover" />
  }
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted">
      {item.kind === 'pdf' ? (
        <PdfFileIcon size={18} aria-hidden="true" />
      ) : (
        <FileIcon size={18} aria-hidden="true" />
      )}
    </span>
  )
}

function Status({ item }: { item: AttachmentChip }) {
  if (item.status === 'error') {
    return (
      <span role="alert" className="block text-xs text-[color:var(--text-danger)]">
        {item.error}
      </span>
    )
  }
  if (item.status === 'uploading') {
    const percent = Math.round(item.progress * 100)
    return (
      <span
        role="progressbar"
        aria-label={`Uploading ${item.fileName}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="mt-1 block h-1 w-24 overflow-hidden rounded-full bg-muted"
      >
        <span
          className="block h-full rounded-full bg-[color:var(--accent-info)] transition-[width]"
          style={{ width: `${Math.max(percent, 4)}%` }}
        />
      </span>
    )
  }
  return <span className="block text-xs text-muted">{formatFileSize(item.size)}</span>
}

/** The files on a form (the chat composer, the support panel), each with its
 * upload's progress or problem and a way to take it off. */
export default function AttachmentChips({
  items,
  notice,
  onRemove,
  className = 'mb-2',
}: {
  items: AttachmentChip[]
  notice: string | null
  onRemove: (localId: string) => void
  className?: string
}) {
  if (items.length === 0 && !notice) return null
  return (
    <div className={className}>
      {items.length > 0 ? (
        <ul aria-label="Files to send" className="flex flex-wrap gap-2">
          {items.map((item) => (
            <li
              key={item.localId}
              className={`flex max-w-64 min-w-0 items-center gap-2 rounded-lg border py-1.5 pr-1 pl-1.5 ${
                item.status === 'error' ? 'border-danger' : 'border-default'
              }`}
            >
              <Preview item={item} />
              <span className="min-w-0 flex-1">
                {/* eslint-disable-next-line no-restricted-syntax -- a truncated value's full text */}
                <span className="block truncate text-sm text-default" title={item.fileName}>
                  {item.fileName}
                </span>
                <Status item={item} />
              </span>
              <IconButton
                label={`Remove ${item.fileName}`}
                onClick={() => onRemove(item.localId)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:bg-muted hover:text-default"
              >
                <CloseIcon size={14} aria-hidden="true" />
              </IconButton>
            </li>
          ))}
        </ul>
      ) : null}
      {notice ? (
        <p role="status" className="mt-1.5 text-xs text-muted">
          {notice}
        </p>
      ) : null}
    </div>
  )
}
